// media-file — what the Translator's file picker accepts.
//
// QA found a .txt sailing through `accept="video/*,audio/*"` (the accept
// attribute is a hint browsers don't enforce) and being uploaded to
// Storage. The list below is the contract: the extension decides, MIME
// only rescues a file that has no extension at all (browsers report
// MIME inconsistently for mkv / flac / aiff, so it cannot be primary).
//
// Pure module — unit-tested in node.

export const SUPPORTED_MEDIA_EXTENSIONS = [
  'mp4',
  'webm',
  'mov',
  'm4a',
  'mp3',
  'wav',
  'aiff',
  'aif',
  'ogg',
  'flac',
  'mkv',
] as const;

export const SUPPORTED_MEDIA_MIME_TYPES = [
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'video/x-matroska',
  'video/ogg',
  'audio/mp4',
  'audio/x-m4a',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/x-wav',
  'audio/wave',
  'audio/aiff',
  'audio/x-aiff',
  'audio/ogg',
  'audio/flac',
  'audio/x-flac',
  'audio/webm',
] as const;

/** Value for `<input type="file" accept=…>`: extensions + MIME types. */
export const MEDIA_FILE_ACCEPT = [
  ...SUPPORTED_MEDIA_EXTENSIONS.map((e) => `.${e}`),
  ...SUPPORTED_MEDIA_MIME_TYPES,
].join(',');

/** Human-readable list for the hint under the drop area. */
export const SUPPORTED_FORMATS_LABEL = 'MP4, WEBM, MOV, M4A, MP3, WAV, AIFF, OGG, FLAC, MKV';

export function fileExtension(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  if (dot <= 0 || dot === base.length - 1) return '';
  return base.slice(dot + 1).toLowerCase();
}

export function isSupportedMediaFile(file: { name: string; type?: string }): boolean {
  const ext = fileExtension(file.name);
  if (ext) return (SUPPORTED_MEDIA_EXTENSIONS as readonly string[]).includes(ext);
  const mime = (file.type ?? '').toLowerCase().split(';')[0]?.trim() ?? '';
  return mime !== '' && (SUPPORTED_MEDIA_MIME_TYPES as readonly string[]).includes(mime);
}

// ─── Size limits + formatting (R-11, phone uploads) ──────────────────

/** Matches the Storage rules ceiling (20 GiB since R-13; was 2 GiB).
 * Enforced in code before any network traffic, with a message that says
 * what to do instead. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024 * 1024;

/** Above this the picker asks for confirmation and shows an ETA once
 * the first seconds of transfer have been measured. */
export const LARGE_UPLOAD_WARN_BYTES = 300 * 1024 * 1024;

/** How long to measure before trusting a speed estimate. */
export const ETA_WARMUP_MS = 5_000;
/** Below this fraction of the file no ETA is shown at all — the first
 * bytes arrive after session setup and would extrapolate to nonsense
 * (the phone test saw "24:37:21" at 0 %). */
export const ETA_MIN_PROGRESS = 0.01;

export type UploadSizeVerdict = 'ok' | 'warn' | 'too_large' | 'empty';

export function uploadSizeVerdict(bytes: number): UploadSizeVerdict {
  if (!Number.isFinite(bytes) || bytes < 0) return 'ok';
  // Q-02: a 0-byte pick vanished silently (Storage accepted it, the
  // worker failed it later). Block it here with a message.
  if (bytes === 0) return 'empty';
  if (bytes > MAX_UPLOAD_BYTES) return 'too_large';
  if (bytes > LARGE_UPLOAD_WARN_BYTES) return 'warn';
  return 'ok';
}

/** 1536 → "1.5 KB", 524288000 → "500 MB", 2254857830 → "2.1 GB". */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  const digits = i === 0 ? 0 : v >= 100 ? 0 : 1;
  return `${v.toFixed(digits)} ${units[i]}`;
}

/** Remaining seconds from measured throughput, or null while the sample
 * is too short (< ETA_WARMUP_MS) or nothing has moved yet. */
export function estimateEtaSeconds(
  bytesTransferred: number,
  totalBytes: number,
  elapsedMs: number,
): number | null {
  if (elapsedMs < ETA_WARMUP_MS || bytesTransferred <= 0 || totalBytes <= 0) return null;
  const bps = bytesTransferred / (elapsedMs / 1000);
  if (!Number.isFinite(bps) || bps <= 0) return null;
  return Math.max(0, Math.round((totalBytes - bytesTransferred) / bps));
}

/** Language-neutral clock: 65 → "1:05", 3725 → "1:02:05". */
export function formatEta(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/**
 * ETA tracker that measures from the FIRST progress event carrying bytes,
 * not from the click: session setup and the first request can take
 * seconds and would otherwise make the initial throughput look tiny.
 * Also refuses to answer below ETA_MIN_PROGRESS of the file.
 */
export interface EtaTracker {
  /** Feed every progress event. Returns the ETA in seconds, or null. */
  update(bytesTransferred: number, totalBytes: number, nowMs?: number): number | null;
  reset(): void;
}

export function createEtaTracker(): EtaTracker {
  let baseBytes: number | null = null;
  let baseTime = 0;
  return {
    update(bytesTransferred, totalBytes, nowMs = Date.now()) {
      if (totalBytes <= 0) return null;
      if (bytesTransferred <= 0) return null;
      if (baseBytes === null) {
        // First event with bytes: this is t0 — nothing to extrapolate yet.
        baseBytes = bytesTransferred;
        baseTime = nowMs;
        return null;
      }
      const elapsed = nowMs - baseTime;
      const moved = bytesTransferred - baseBytes;
      if (elapsed < ETA_WARMUP_MS || moved <= 0) return null;
      if (bytesTransferred / totalBytes < ETA_MIN_PROGRESS) return null;
      const bps = moved / (elapsed / 1000);
      if (!Number.isFinite(bps) || bps <= 0) return null;
      return Math.max(0, Math.round((totalBytes - bytesTransferred) / bps));
    },
    reset() {
      baseBytes = null;
      baseTime = 0;
    },
  };
}

// ─── Pre-submit estimates (R-13; aligned with the worker's costEstimator) ─

/** Processing wall-clock per second of media. 0.26 ≈ 35 min for a
 * 2:14 h file, the figure quoted in the R-13 ticket. Tune against real
 * jobs; the API's `estimatedProcessingSeconds` wins when present. */
export const PROCESSING_SECONDS_PER_MEDIA_SECOND = 0.26;

/** Estimated Gemini cost per media minute in USD. Single constant, kept
 * in sync with apps/worker/lib/costEstimator.js in nayto-translator; the
 * API's `estimatedCostUsd` (R-12) wins when present. */
export const ESTIMATED_COST_USD_PER_MEDIA_MINUTE = 0.012;

export function estimateProcessingSeconds(mediaSeconds: number): number | null {
  if (!Number.isFinite(mediaSeconds) || mediaSeconds <= 0) return null;
  return Math.round(mediaSeconds * PROCESSING_SECONDS_PER_MEDIA_SECOND);
}

export function estimateCostUsd(mediaSeconds: number): number | null {
  if (!Number.isFinite(mediaSeconds) || mediaSeconds <= 0) return null;
  return Math.round((mediaSeconds / 60) * ESTIMATED_COST_USD_PER_MEDIA_MINUTE * 1000) / 1000;
}
